import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { AutoVideo } from './AutoVideo.jsx';
import { MEDIA_ACCEPT, uploadResidentMedia } from '../livestock-media.js';
import { startReorderDrag, reorderPreview } from '../drag-reorder.js';
import { ResidentEndSheet } from './ResidentEndSheet.jsx';
import { ResidentNotes } from './ResidentNotes.jsx';
import { keptForLabel } from '../livestock-journey.js';
import { localDateIso } from '../local-date.js';
import { useConfirm } from './ConfirmDialog.jsx';
import { catalogFits } from '../saltwater-tools.js';

// The photo / video delete sits on the thumbnail a wet thumb scrolls past,
// so its target is a full 44 px corner while the visible dot stays small.
const GALLERY_X_HIT = {
  top: 0,
  right: 0,
  width: 44,
  height: 44,
  padding: 4,
  background: 'transparent',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
};
const GALLERY_X_DOT = {
  width: 22,
  height: 22,
  borderRadius: 999,
  background: 'rgba(0, 0, 0, 0.7)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// "What's in the tank" panel for Home. Each resident card shows a
// live video thumbnail when the user has uploaded a clip, otherwise
// the cover photo (most recent upload), otherwise the catalog
// thumbnail, otherwise a kind emoji. Tap → edit sheet with the full
// gallery + qty controls.
//
// Storage:
//   - Image bytes in Postgres (BYTEA, small)
//   - Video bytes on the Railway volume; poster + metadata in Postgres

const KIND_EMOJI = { fish: '🐟', coral: '🪸', invert: '🦐' };
const KIND_LABEL = { fish: 'Fish', coral: 'Coral', invert: 'Inverts' };

// The keeper's own calendar day, not UTC's (see ResidentEndSheet).
const localToday = () => localDateIso();

function residentVideoSrc(tankId, r) {
  if (!r.cover_video_id) return null;
  return `/api/tanks/${tankId}/livestock/${r.id}/video/${r.cover_video_id}`;
}
// Server-stored poster frame for the user's clip. Lets the home-card
// show the actual first frame (not a black square) while the video
// element decodes, AND covers the case where the resident has no
// catalog/user image at all but does have a video.
function residentVideoPosterSrc(tankId, r) {
  if (!r.cover_video_id) return null;
  return `/api/tanks/${tankId}/livestock/${r.id}/video/${r.cover_video_id}/poster`;
}
function residentImageSrc(tankId, r) {
  if (r.cover_image_id) {
    return `/api/tanks/${tankId}/livestock/${r.id}/image/${r.cover_image_id}`;
  }
  // Stock catalog photo: serve same-origin via the cache proxy (keyed by
  // catalog id) so a flaky third-party hotlink doesn't leave the card
  // blank. Falls back to the raw URL for any resident with no catalog link.
  if (r.catalog_image_url && r.catalog_id) {
    return `/api/livestock-image/${r.catalog_id}`;
  }
  return r.catalog_image_url || null;
}

const CHIP_KINDS = [
  { kind: 'fish', emoji: '🐟', singular: 'fish', plural: 'fish' },
  { kind: 'coral', emoji: '🪸', singular: 'coral', plural: 'corals' },
  { kind: 'invert', emoji: '🦐', singular: 'invert', plural: 'inverts' },
];

export function LivestockResidents({
  tank,
  careKinds = {},
  onOpenLivestock,
  onOpenHistory,
  onReload,
  reordering = false,
}) {
  const lv = tank.livestock || [];
  const [editing, setEditing] = useState(null);
  // Optimistic per-kind id order while reordering. Server order comes
  // from livestock.position (NULLS LAST → common_name); each move
  // re-spaces the whole kind group's positions so the arrangement
  // persists per-tank and syncs across devices.
  const [groupOrder, setGroupOrder] = useState({});
  useEffect(() => setGroupOrder({}), [tank.id]);
  // Home shows the counts; the list opens on a tap (owner, 2026-10-21: every
  // resident as a tall tile made Home five screens long). Reordering shows
  // it, to drag.
  const [listOpen, setListOpen] = useState(false);
  useEffect(() => setListOpen(false), [tank.id]);
  const showList = listOpen || reordering;
  const [scrollTo, setScrollTo] = useState(null);
  // Tapping a summary chip scrolls to that kind's group below (the
  // residents are already on this panel) — it must NOT open the catalog
  // browser, which is what "Browse" / the empty-state CTA are for.
  const groupRefs = useRef({});
  const scrollToKind = (kind) => {
    if (!showList) {
      setListOpen(true);
      setScrollTo(kind);
      return;
    }
    const el = groupRefs.current[kind];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };
  useEffect(() => {
    if (!scrollTo || !showList) return;
    groupRefs.current[scrollTo]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setScrollTo(null);
  }, [scrollTo, showList]);

  const groups = lv.reduce((acc, r) => {
    const k = r.kind || 'other';
    (acc[k] = acc[k] || []).push(r);
    return acc;
  }, {});
  for (const [kind, ids] of Object.entries(groupOrder)) {
    if (!groups[kind]) continue;
    const byId = new Map(groups[kind].map((r) => [r.id, r]));
    const sorted = ids.map((id) => byId.get(id)).filter(Boolean);
    for (const r of groups[kind]) if (!ids.includes(r.id)) sorted.push(r);
    groups[kind] = sorted;
  }
  const orderedKinds = ['fish', 'coral', 'invert'].filter((k) => groups[k]?.length);

  // Persist the whole group's positions (small N). Sequential PATCHes
  // keep the route simple; a failure drops the optimistic order so
  // the UI falls back to what the server actually has.
  const persistGroup = (kind, ids) => {
    (async () => {
      try {
        for (let idx = 0; idx < ids.length; idx++) {
          await api(`/api/tanks/${tank.id}/livestock/${ids[idx]}`, {
            method: 'PATCH',
            body: { position: (idx + 1) * 10 },
          });
        }
      } catch {
        setGroupOrder((prev) => {
          const next = { ...prev };
          delete next[kind];
          return next;
        });
      }
    })();
  };

  const moveResident = (kind, id, dir) => {
    const ids = groups[kind].map((r) => r.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setGroupOrder((prev) => ({ ...prev, [kind]: ids }));
    persistGroup(kind, ids);
  };

  const dragSnapshotRef = useRef(null);
  const startResidentDrag = (e, kind, r) => {
    const container = e.currentTarget.closest('.hv-residents-list');
    dragSnapshotRef.current = groupOrder[kind] || null;
    startReorderDrag({
      event: e,
      container,
      key: r.id,
      label: r.common_name,
      onPreview: (dragKey, overKey) => {
        setGroupOrder((prev) => {
          const current = prev[kind] || groups[kind].map((x) => x.id);
          return { ...prev, [kind]: reorderPreview(current, dragKey, overKey) };
        });
      },
      onCommit: () => {
        setGroupOrder((latest) => {
          const ids = latest[kind];
          if (ids) persistGroup(kind, ids);
          return latest;
        });
      },
      onCancel: () => {
        setGroupOrder((prev) => {
          const next = { ...prev };
          if (dragSnapshotRef.current) next[kind] = dragSnapshotRef.current;
          else delete next[kind];
          return next;
        });
      },
    });
  };

  const counts = lv.reduce(
    (acc, r) => {
      const n = Number(r.qty) || 1;
      if (r.kind === 'fish') acc.fish += n;
      else if (r.kind === 'coral') acc.coral += n;
      else if (r.kind === 'invert') acc.invert += n;
      return acc;
    },
    { fish: 0, coral: 0, invert: 0 }
  );

  return (
    <section className="hv-residents" aria-label="What's in the tank">
      <div className="hv-residents-head">
        <div className="hv-residents-eyebrow font-heading">In this tank</div>
        <div className="hv-residents-ctas">
          {onOpenHistory && (
            <button type="button" className="hv-residents-cta font-heading" onClick={onOpenHistory}>
              History
            </button>
          )}
          <button type="button" className="hv-residents-cta font-heading" onClick={onOpenLivestock}>
            Browse
          </button>
        </div>
      </div>
      {lv.length > 0 && (
        <div className="hv-residents-chips" aria-label="Livestock summary">
          {CHIP_KINDS.map(({ kind, emoji, singular, plural }) => {
            const n = counts[kind];
            if (n === 0) return null;
            const label = n === 1 ? singular : plural;
            return (
              <button
                key={kind}
                type="button"
                className="hv-residents-chip"
                onClick={() => scrollToKind(kind)}
                aria-label={`${n} ${label} — jump to ${label} in this tank`}
              >
                <span aria-hidden="true">{emoji}</span> {n} {label}
              </button>
            );
          })}
        </div>
      )}
      {/* The "In quarantine" badges sit in the list, closed by default: say
        it here too, so a fish in QT is never out of sight on Home. */}
      {!showList &&
        (() => {
          const inQt = lv.filter((r) => careKinds[r.id] === 'quarantine').length;
          const inTx = lv.filter((r) => careKinds[r.id] && careKinds[r.id] !== 'quarantine').length;
          if (!inQt && !inTx) return null;
          return (
            <div className="hv-resident-care font-heading" style={{ marginTop: 8 }}>
              {[inQt ? `${inQt} in quarantine` : null, inTx ? `${inTx} in treatment` : null]
                .filter(Boolean)
                .join(' · ')}
            </div>
          );
        })()}
      {orderedKinds.length > 0 && !reordering && (
        <button
          type="button"
          className="ts-chip hv-residents-toggle"
          aria-expanded={showList}
          onClick={() => setListOpen((v) => !v)}
        >
          {showList ? 'Hide the list' : 'Show the list'}
        </button>
      )}
      {orderedKinds.length === 0 ? (
        <div className="hv-residents-empty">
          <p>
            {tank.role === 'viewer'
              ? 'Nothing in here yet.'
              : catalogFits(tank)
                ? 'Nothing in here yet. Browse the catalog to add your first species.'
                : 'Nothing in here yet. Add your fish and shrimp by name.'}
          </p>
          {tank.role !== 'viewer' && (
            <button
              type="button"
              className="hv-residents-empty-cta font-heading"
              onClick={onOpenLivestock}
            >
              + Add livestock
            </button>
          )}
        </div>
      ) : !showList ? null : (
        orderedKinds.map((kind) => (
          <div
            key={kind}
            className="hv-residents-group"
            ref={(el) => (groupRefs.current[kind] = el)}
          >
            <div className="hv-residents-group-label font-heading">{KIND_LABEL[kind]}</div>
            <ul className="hv-residents-list">
              {groups[kind].map((r) => {
                const videoSrc = residentVideoSrc(tank.id, r);
                const imgSrc = residentImageSrc(tank.id, r);
                // Prefer the user image as the poster (matches their tank);
                // if there's no image, fall back to the video's own poster
                // frame from the server so the slot never renders as a
                // black rectangle while the clip's first frame loads.
                const posterSrc = imgSrc || residentVideoPosterSrc(tank.id, r);
                return (
                  <li key={r.id} data-drag-key={r.id}>
                    <button
                      type="button"
                      className="hv-resident"
                      onClick={reordering ? undefined : () => setEditing(r)}
                      aria-label={
                        reordering
                          ? `${r.common_name} — drag the handle to reorder`
                          : `${r.common_name} — ${r.qty || 1} in tank. Edit.`
                      }
                      style={reordering ? { cursor: 'default' } : undefined}
                    >
                      <div className="hv-resident-thumb" aria-hidden="true">
                        {videoSrc ? (
                          <AutoVideo
                            src={videoSrc}
                            poster={posterSrc || undefined}
                            fallback={
                              <span className="hv-resident-emoji">{KIND_EMOJI[kind] || '🌊'}</span>
                            }
                          />
                        ) : imgSrc ? (
                          <img
                            src={imgSrc}
                            alt=""
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <>
                            <span className="hv-resident-emoji">{KIND_EMOJI[kind] || '🌊'}</span>
                            {/* Camera-plus cue so a reefer sees that a
                                photo can be added. The whole row tap
                                already opens the editor where the
                                upload affordance lives, so this badge
                                is purely a visual hint — no extra
                                handler, no nested interactive element. */}
                            <span
                              className="hv-resident-add-photo"
                              aria-hidden="true"
                              title="Add your own photo"
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                <line x1="12" y1="11" x2="12" y2="17" />
                                <line x1="9" y1="14" x2="15" y2="14" />
                              </svg>
                            </span>
                          </>
                        )}
                      </div>
                      <div className="hv-resident-body">
                        <div className="hv-resident-name">{r.common_name}</div>
                        {r.nickname && <div className="hv-resident-sci">{r.nickname}</div>}
                        {!r.nickname && r.scientific_name && (
                          <div className="hv-resident-sci">{r.scientific_name}</div>
                        )}
                        {careKinds[r.id] && (
                          <div className="hv-resident-care font-heading">
                            {careKinds[r.id] === 'quarantine' ? 'In quarantine' : 'In treatment'}
                          </div>
                        )}
                      </div>
                      {reordering ? (
                        <span
                          role="button"
                          tabIndex={0}
                          className="hv-drag-handle compact"
                          onPointerDown={(e) => startResidentDrag(e, kind, r)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                              e.preventDefault();
                              moveResident(kind, r.id, -1);
                            } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                              e.preventDefault();
                              moveResident(kind, r.id, 1);
                            }
                          }}
                          aria-label={`Drag to move ${r.common_name}; arrow keys also work`}
                        >
                          <span className="hv-drag-grip" aria-hidden="true">
                            ⠿
                          </span>
                        </span>
                      ) : (
                        <div className="hv-resident-qty" aria-hidden="true">
                          ×{r.qty || 1}
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))
      )}
      {editing && (
        <ResidentEditSheet
          tank={tank}
          resident={editing}
          onClose={() => setEditing(null)}
          onReload={onReload}
          onOpenHistory={onOpenHistory}
        />
      )}
    </section>
  );
}

function addedLine(r) {
  if (!r.added_at) return 'Date added not recorded';
  const d = new Date(`${String(r.added_at).slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return 'Date added not recorded';
  const date = d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  const kept = keptForLabel(r.added_at);
  return `Added ${date}${kept ? `, in the tank ${kept}` : ''}`;
}

export function ResidentEditSheet({ tank, resident, onClose, onReload, onOpenHistory }) {
  const confirm = useConfirm();
  const [qty, setQty] = useState(Number(resident.qty) || 1);
  const [nickname, setNickname] = useState(resident.nickname || '');
  const savedAdded = resident.added_at ? String(resident.added_at).slice(0, 10) : '';
  const [addedOn, setAddedOn] = useState(savedAdded);
  const canWrite = tank.role !== 'viewer';
  const [ending, setEnding] = useState(false);
  const [fragged, setFragged] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  // Opt-in (owner, 2026-10-01): a keeper's photos reach the catalog review
  // queue only when they tick the box. Photos already shared stay shared.
  const [shareWithCatalog, setShareWithCatalog] = useState(false);

  const refreshMedia = async () => {
    try {
      const [imgs, vids] = await Promise.all([
        api(`/api/tanks/${tank.id}/livestock/${resident.id}/images`),
        api(`/api/tanks/${tank.id}/livestock/${resident.id}/videos`),
      ]);
      setImages(imgs || []);
      setVideos(vids || []);
    } catch (e) {
      setErr(e.message || 'Could not load media.');
    }
  };
  useEffect(() => {
    refreshMedia();
  }, [tank.id, resident.id]);

  const saveQty = async () => {
    const nick = nickname.trim();
    const dateChanged = !!addedOn && addedOn !== savedAdded;
    if (qty === resident.qty && nick === (resident.nickname || '') && !dateChanged) return true;
    try {
      await api(`/api/tanks/${tank.id}/livestock/${resident.id}`, {
        method: 'PATCH',
        body: { qty, nickname: nick || null, ...(dateChanged ? { added_at: addedOn } : {}) },
      });
      return true;
    } catch (e) {
      setErr(e.message || 'Could not save.');
      return false;
    }
  };

  const uploadMedia = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr('');
    try {
      await uploadResidentMedia({
        tankId: tank.id,
        residentId: resident.id,
        file,
        shareWithCatalog,
      });
      await refreshMedia();
      if (onReload) await onReload();
    } catch (e) {
      setErr(e.message || 'Could not upload.');
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (imageId) => {
    const ok = await confirm({
      title: 'Delete this photo from its record?',
      body: 'It cannot be brought back.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setErr('');
    try {
      const r = await fetch(`/api/tanks/${tank.id}/livestock/${resident.id}/image/${imageId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`Delete failed (${r.status}).`);
      await refreshMedia();
      if (onReload) await onReload();
    } catch (e) {
      setErr(e.message || 'Could not delete.');
    } finally {
      setBusy(false);
    }
  };

  const deleteVideo = async (videoId) => {
    const ok = await confirm({
      title: 'Delete this video from its record?',
      body: 'It cannot be brought back.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setErr('');
    try {
      const r = await fetch(`/api/tanks/${tank.id}/livestock/${resident.id}/video/${videoId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`Delete failed (${r.status}).`);
      await refreshMedia();
      if (onReload) await onReload();
    } catch (e) {
      setErr(e.message || 'Could not delete.');
    } finally {
      setBusy(false);
    }
  };

  // A frag cut from this coral goes in as its own resident, with the
  // lineage kept, so each piece has its own journey from here on.
  const takeFrag = async () => {
    setBusy(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/livestock`, {
        method: 'POST',
        body: { frag_of: resident.id, qty: 1, added_at: localToday() },
      });
      setFragged('Frag added. It has its own card now.');
      if (onReload) await onReload();
    } catch (e) {
      setErr(e.message || 'Could not add the frag.');
    } finally {
      setBusy(false);
    }
  };

  const onDone = async () => {
    const ok = await saveQty();
    if (ok) {
      if (onReload) await onReload();
      onClose();
    }
  };

  const hasMedia = images.length > 0 || videos.length > 0;

  return (
    <div
      className="hv-resident-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        // A tap outside keeps what was typed, like Done: a date picked with
        // one wet hand must not vanish because the sheet closed.
        if (e.target === e.currentTarget && !busy && !uploading) onDone();
      }}
    >
      <div className="hv-resident-sheet">
        <header className="hv-resident-sheet-head">
          <div>
            <div className="hv-resident-sheet-eyebrow font-heading">
              {KIND_LABEL[resident.kind] || 'Resident'}
            </div>
            <h3 className="hv-resident-sheet-title">{resident.common_name}</h3>
            {resident.scientific_name && (
              <div className="hv-resident-sheet-sci">{resident.scientific_name}</div>
            )}
            {resident.frag_of_name && (
              <div className="hv-resident-sheet-sci">Cut from {resident.frag_of_name}</div>
            )}
          </div>
          <button
            type="button"
            className="hv-resident-sheet-close"
            onClick={onDone}
            aria-label="Close"
            disabled={busy || uploading}
          >
            ×
          </button>
        </header>

        <div className="hv-resident-gallery-eyebrow font-heading">Media</div>
        <div className="hv-resident-gallery">
          {videos.map((v) => (
            <div key={v.id} className="hv-resident-gallery-item">
              <AutoVideo src={`/api/tanks/${tank.id}/livestock/${resident.id}/video/${v.id}`} />
              {canWrite && (
                <button
                  type="button"
                  className="hv-resident-gallery-x"
                  style={GALLERY_X_HIT}
                  onClick={() => deleteVideo(v.id)}
                  disabled={busy || uploading}
                  aria-label="Delete video"
                >
                  <span style={GALLERY_X_DOT}>×</span>
                </button>
              )}
              <span className="hv-resident-gallery-badge">video</span>
            </div>
          ))}
          {images.map((img) => (
            <div key={img.id} className="hv-resident-gallery-item">
              <img
                src={`/api/tanks/${tank.id}/livestock/${resident.id}/image/${img.id}`}
                alt=""
                loading="lazy"
              />
              {canWrite && (
                <button
                  type="button"
                  className="hv-resident-gallery-x"
                  style={GALLERY_X_HIT}
                  onClick={() => deleteImage(img.id)}
                  disabled={busy || uploading}
                  aria-label="Delete photo"
                >
                  <span style={GALLERY_X_DOT}>×</span>
                </button>
              )}
            </div>
          ))}
          {!hasMedia && (
            <div className="hv-resident-gallery-empty">
              {canWrite ? 'No photos or videos yet. Add one below.' : 'No photos or videos yet.'}
            </div>
          )}
        </div>

        {/* A viewer can look; every write here would answer read-only. */}
        {canWrite && (
          <>
            <div className="hv-resident-photo-actions">
              <label
                className="hv-resident-photo-btn font-heading"
                aria-disabled={busy || uploading}
                style={{
                  opacity: busy || uploading ? 0.5 : 1,
                  cursor: busy || uploading ? 'not-allowed' : 'pointer',
                }}
              >
                {uploading ? 'Uploading…' : '+ Add photo or video'}
                <input
                  type="file"
                  accept={MEDIA_ACCEPT}
                  style={{ display: 'none' }}
                  disabled={busy || uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) uploadMedia(f);
                  }}
                />
              </label>
            </div>
            <label className="hv-resident-share">
              <input
                type="checkbox"
                checked={shareWithCatalog}
                onChange={(e) => setShareWithCatalog(e.target.checked)}
                disabled={busy || uploading}
              />
              <span>Share new uploads with the catalog (admin reviews before promoting).</span>
            </label>
          </>
        )}

        <div className="hv-resident-gallery-eyebrow font-heading">Its journey</div>
        <p className="res-journey-line">
          {addedLine({ ...resident, added_at: addedOn || resident.added_at })}
          {resident.source ? `. From ${resident.source}` : ''}
          {resident.frag_of_name ? `. Cut from ${resident.frag_of_name}` : ''}.
          {onOpenHistory && (
            <>
              {' '}
              <button
                type="button"
                className="res-journey-link"
                onClick={() => {
                  onClose();
                  onOpenHistory();
                }}
                disabled={busy || uploading}
              >
                All your livestock history
              </button>
            </>
          )}
        </p>
        {resident.notes && (
          <p className="res-journey-line res-journey-first">
            <span className="rj-label font-heading">Noted when added</span>
            {resident.notes}
          </p>
        )}
        <ResidentNotes
          base={`/api/tanks/${tank.id}/livestock/${resident.id}`}
          canWrite={canWrite}
          isOwner={!tank.role || tank.role === 'owner'}
          addedAt={addedOn || resident.added_at}
          disabled={busy || uploading}
          onPhotosChanged={async () => {
            await refreshMedia();
            if (onReload) await onReload();
          }}
        />

        <label className="rj-field">
          <span className="rj-label font-heading">Nickname (optional)</span>
          <input
            type="text"
            maxLength={60}
            placeholder="e.g. big colony, left rock"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            disabled={busy || uploading || !canWrite}
          />
        </label>

        <label className="rj-field">
          <span className="rj-label font-heading">Date added</span>
          <input
            type="date"
            max={localToday()}
            value={addedOn}
            onChange={(e) => setAddedOn(e.target.value)}
            disabled={busy || uploading || !canWrite}
          />
        </label>

        <div className="hv-resident-row">
          <div className="hv-resident-field-label font-heading">Qty</div>
          {!canWrite ? (
            <span className="font-mono">{qty}</span>
          ) : (
            <div className="hv-resident-qty-stepper">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                disabled={busy || uploading || qty <= 1}
                aria-label="Decrease quantity"
              >
                −
              </button>
              <span className="font-mono">{qty}</span>
              <button
                type="button"
                onClick={() => setQty((q) => Math.min(9999, q + 1))}
                disabled={busy || uploading}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          )}
        </div>

        {err && (
          <p className="hv-resident-err" role="alert">
            {err}
          </p>
        )}

        {canWrite && resident.kind === 'coral' && (
          <button
            type="button"
            className="rj-secondary font-heading"
            onClick={takeFrag}
            disabled={busy || uploading}
          >
            + Took a frag from it
          </button>
        )}
        {fragged && <p className="rj-ok">{fragged}</p>}

        <div className="hv-resident-actions">
          {canWrite && (
            <button
              type="button"
              className="hv-resident-action danger font-heading"
              onClick={async () => {
                // Save a just-typed nickname first, so the record that ends
                // carries it.
                if (await saveQty()) setEnding(true);
              }}
              disabled={busy || uploading}
            >
              No longer in the tank
            </button>
          )}
          <button
            type="button"
            className="hv-resident-action save font-heading"
            onClick={onDone}
            disabled={busy || uploading}
          >
            {busy ? '…' : 'Done'}
          </button>
        </div>
      </div>
      {ending && (
        <ResidentEndSheet
          tank={tank}
          resident={{ ...resident, qty, nickname: nickname.trim() || null }}
          onClose={() => setEnding(false)}
          onDone={async () => {
            if (onReload) await onReload();
            onClose();
          }}
        />
      )}
    </div>
  );
}
